/**
 * Advanced → Website content — publish a PAGE PER RECORD of any transaction
 * on this application's website: a blog, department pages, a product
 * catalogue, job openings, events, a team directory…
 *
 * One mechanism, many kinds. A collection is an ordinary transaction plus a
 * content profile saying which columns play which role on a public page
 * (slug, title, content, image, dates…). The kinds offered below are only
 * starting points: after setup everything is a normal transaction and normal
 * smart pages, edited like any other. People author the records in the Run
 * view (menu "Website content"), and the site rebuilds itself when they do.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  AlertTriangle, BookOpen, Check, Globe, Loader2, Plus, RefreshCw, Settings2, X,
} from 'lucide-react'
import {
  contentErr, createCollection, disableCollection, setCollectionComments, setCollectionNewsletter, enableCollection, getContentObject,
  getContentOverview, listContentPresets, refreshSiteContent, saveSiteSettings,
  validateContentProfile,
  type ColumnInfo, type ContentObject, type ContentPreset, type ContentProfile,
  type ContentSite, type ContentTaxonomy,
} from '@/api/contentCollections'

const muted: React.CSSProperties = { color: 'var(--color-text-secondary)', fontSize: 13 }

function slugify(s: string): string {
  return s.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60)
}

export default function WebsiteContentPage() {
  const { project = '' } = useParams<{ project: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [objects, setObjects] = useState<ContentObject[]>([])
  const [sites, setSites] = useState<ContentSite[]>([])
  const [presets, setPresets] = useState<ContentPreset[]>([])
  const [roleHelp, setRoleHelp] = useState<Record<string, string>>({})
  const [sdTypes, setSdTypes] = useState<string[]>([])
  const [mode, setMode] = useState<null | 'new' | { edit: string }>(null)

  const load = useCallback(async () => {
    if (!project) return
    setLoading(true)
    try {
      const [ov, pr] = await Promise.all([getContentOverview(project), listContentPresets()])
      setObjects(ov.objects)
      setSites(ov.sites)
      setPresets(pr.presets)
      setRoleHelp(pr.roles)
      setSdTypes(pr.structured_types)
    } catch (err) {
      toast.error(contentErr(err, 'Could not load this application’s website content'))
    } finally {
      setLoading(false)
    }
  }, [project])

  useEffect(() => { void load() }, [load])
  useEffect(() => { document.title = `Website content · ${project}` }, [project])

  const collections = objects.filter(o => o.content)

  if (loading) {
    return (
      <div style={{ padding: 32, display: 'flex', gap: 10, alignItems: 'center' }}>
        <Loader2 size={18} className="spin" />
        <span style={muted}>Loading…</span>
      </div>
    )
  }

  return (
    <div style={{ padding: '0 32px 32px', maxWidth: 1040 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/workbench')}>&larr; Back</button>
        <h1 style={{ fontSize: 23, fontWeight: 600, margin: 0 }}>Website content: {project}</h1>
      </div>
      <p style={{ ...muted, fontSize: 14, margin: '0 0 20px 44px' }}>
        Publish a page for every record of a transaction — blog posts, departments, products,
        job openings, events — each with its own address, search-engine description and
        structured data. Records are written in the running application (menu
        <strong> Website content</strong>) with a rich-text editor, and the site republishes
        itself when they change.
      </p>

      {/* ── Sites ── */}
      <h2 style={{ fontSize: 16, fontWeight: 600, margin: '8px 0 10px' }}>Sites</h2>
      {sites.length === 0 ? (
        <div className="card" style={{ padding: 16, marginBottom: 20, ...muted }}>
          This application has no website yet. Add an external site in the navigation first —
          collections publish into a site.
        </div>
      ) : sites.map(s => (
        <SiteCard key={s.slug} project={project} site={s} onSaved={load} />
      ))}

      {/* ── Collections ── */}
      <div style={{ display: 'flex', alignItems: 'center', margin: '22px 0 10px', gap: 10 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0, flex: 1 }}>Collections</h2>
        <button className="btn btn-primary btn-sm" onClick={() => setMode('new')}>
          <Plus size={14} /> New collection
        </button>
        <button className="btn btn-ghost btn-sm" onClick={() => setMode({ edit: '' })}>
          <BookOpen size={14} /> Publish an existing transaction
        </button>
      </div>

      {mode === 'new' && (
        <NewCollection
          project={project} presets={presets} sites={sites}
          onClose={() => setMode(null)}
          onCreated={() => { setMode(null); void load() }}
        />
      )}
      {mode && typeof mode === 'object' && (
        <ProfileEditor
          project={project} objects={objects} sites={sites} initialObj={mode.edit}
          roleHelp={roleHelp} sdTypes={sdTypes} presets={presets}
          onClose={() => setMode(null)}
          onSaved={() => { setMode(null); void load() }}
        />
      )}

      {collections.length === 0 && !mode ? (
        <div className="card" style={{ padding: 18, ...muted }}>
          No collections yet. Start one from a kind (Blog, Departments, Products …), or publish a
          transaction you already have.
        </div>
      ) : collections.map(o => (
        <div key={o.obj_name} className="card" style={{ padding: 14, marginBottom: 10, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>
              {o.label?.plural || o.title}
              <span style={{ ...muted, fontWeight: 400, marginLeft: 8 }}>
                {o.obj_name}{o.kind ? ` · ${presets.find(p => p.id === o.kind)?.label || o.kind}` : ''}
              </span>
            </div>
            {o.errors.map(e => (
              <div key={e} style={{ color: 'var(--color-error, #dc2626)', fontSize: 13, marginTop: 4 }}>
                <AlertTriangle size={12} /> {e}
              </div>
            ))}
            {o.warnings.map(w => (
              <div key={w} style={{ color: 'var(--color-warning, #d97706)', fontSize: 13, marginTop: 4 }}>{w}</div>
            ))}
            {o.content && (
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8, fontSize: 13 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                  title="Visitors can comment on each page; you approve them in the Comments screen of the Run view">
                  <input type="checkbox" checked={!!o.comments?.enabled}
                    onChange={async e => {
                      try {
                        await setCollectionComments(project, o.obj_name, e.target.checked)
                        toast.success(e.target.checked
                          ? 'Comments on — a Comments screen is in the Run view; republish to show the form'
                          : 'Comments off at the next publish')
                        void load()
                      } catch (err) { toast.error(contentErr(err, 'Could not change comments')) }
                    }} />
                  Reader comments (moderated)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                  title="Subscribers get an e-mail when a new record goes live. Add a Newsletter block saving into the Subscribers list">
                  <input type="checkbox" checked={!!o.newsletter?.enabled}
                    onChange={async e => {
                      try {
                        const res = await setCollectionNewsletter(project, o.obj_name, e.target.checked)
                        toast.success(e.target.checked
                          ? `New-post e-mails on — point a Newsletter block at "${res.object || 'Subscribers'}"`
                          : 'New-post e-mails off')
                        void load()
                      } catch (err) { toast.error(contentErr(err, 'Could not change the newsletter')) }
                    }} />
                  E-mail new {(o.label?.plural || 'records').toLowerCase()} to subscribers
                </label>
              </div>
            )}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => setMode({ edit: o.obj_name })}>
            <Settings2 size={14} /> Publishing
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={async () => {
              if (!window.confirm(`Stop publishing ${o.label?.plural || o.obj_name}? Its pages disappear from the site at the next publish; the records are kept.`)) return
              try {
                await disableCollection(project, o.obj_name)
                toast.success('Publishing turned off')
                void load()
              } catch (err) { toast.error(contentErr(err, 'Could not turn publishing off')) }
            }}
          >Turn off</button>
        </div>
      ))}
    </div>
  )
}

// ── One site's publish settings ─────────────────────────────────────────

type Redirect = { from: string; to: string }

function redirectsToText(value: unknown): string {
  if (!Array.isArray(value)) return ''
  return (value as Redirect[])
    .filter(r => r && typeof r.from === 'string' && typeof r.to === 'string')
    .map(r => `${r.from} -> ${r.to}`)
    .join('\n')
}

// `old -> new` (or two whitespace-separated columns, as pasted from a
// spreadsheet). Blank lines and `#` comments are skipped; the server
// validates and normalises what is left.
function textToRedirects(text: string): Redirect[] {
  const out: Redirect[] = []
  for (const raw of text.split('\n')) {
    const line = raw.trim()
    if (!line || line.startsWith('#')) continue
    const parts = line.includes('->') ? line.split('->') : line.split(/\s+/)
    if (parts.length < 2) continue
    out.push({ from: parts[0].trim(), to: parts.slice(1).join(' ').trim() })
  }
  return out
}

function SiteCard({ project, site, onSaved }: { project: string; site: ContentSite; onSaved: () => void }) {
  const s = site.settings as Record<string, string | boolean | undefined>
  const [siteUrl, setSiteUrl] = useState(String(s.siteUrl || ''))
  const [siteName, setSiteName] = useState(String(s.siteName || ''))
  const [lang, setLang] = useState(String(s.lang || ''))
  const [logo, setLogo] = useState(String(s.logo || ''))
  const [ogImage, setOgImage] = useState(String(s.defaultOgImage || ''))
  const [currency, setCurrency] = useState(String(s.currency || ''))
  // Legacy URLs kept alive after a move — one `old -> new` per line.
  const [redirects, setRedirects] = useState(redirectsToText(site.settings.redirects))
  const [auto, setAuto] = useState(site.auto_publish_content)
  const [saving, setSaving] = useState(false)
  const [refreshing, setRefreshing] = useState(false)

  const save = async () => {
    setSaving(true)
    try {
      await saveSiteSettings(project, site.slug, {
        siteUrl: siteUrl.trim(), siteName: siteName.trim(), lang: lang.trim(),
        logo: logo.trim(), defaultOgImage: ogImage.trim(), currency: currency.trim().toUpperCase(),
        redirects: textToRedirects(redirects),
      }, auto)
      toast.success('Site settings saved — they apply at the next publish')
      onSaved()
    } catch (err) {
      toast.error(contentErr(err, 'Could not save the site settings'))
    } finally {
      setSaving(false)
    }
  }

  const field = (label: string, value: string, set: (v: string) => void, placeholder: string, help?: string) => (
    <div>
      <label className="form-label">{label}</label>
      <input className="form-input" value={value} placeholder={placeholder} onChange={e => set(e.target.value)} />
      {help && <small style={{ ...muted, fontSize: 13 }}>{help}</small>}
    </div>
  )

  return (
    <div className="card" style={{ padding: 18, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Globe size={16} />
        <strong>{site.title || site.slug}</strong>
        <span style={muted}>/{site.slug}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
        {field('Site address', siteUrl, setSiteUrl, 'https://www.example.com',
          'Needed for canonical links, the sitemap and RSS feeds. Left empty, the site’s own domain is used when it has one.')}
        {field('Site name', siteName, setSiteName, 'Acme', 'Shown in titles, social cards and as the publisher of articles.')}
        {field('Language', lang, setLang, 'en', 'e.g. en, en-IN, fr')}
        {field('Logo URL', logo, setLogo, '/api/designers/assets/raw?…', 'Publisher logo in structured data.')}
        {field('Default social image', ogImage, setOgImage, 'https://…/share.png')}
        {field('Default currency', currency, setCurrency, 'INR', 'For product structured data.')}
      </div>
      <div style={{ marginTop: 14 }}>
        <label className="form-label">Redirects</label>
        <textarea
          className="form-input" rows={4} value={redirects}
          placeholder={'/old-post/ -> /blog/old-post/\n/about-us/ -> /about/'}
          onChange={e => setRedirects(e.target.value)}
          style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 13 }}
        />
        <small style={{ ...muted, fontSize: 13 }}>
          One per line: an old address on this site, then where it now lives. Keeps links and search
          rankings working after pages move. A published page always wins over a redirect.
        </small>
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, fontSize: 14 }}>
        <input type="checkbox" checked={auto} onChange={e => setAuto(e.target.checked)} />
        Republish automatically when content changes or a scheduled record goes live
      </label>
      <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
        <button className="btn btn-primary btn-sm" disabled={saving} onClick={() => void save()}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          className="btn btn-ghost btn-sm" disabled={refreshing}
          title="Rebuild the published site over current content, now"
          onClick={async () => {
            setRefreshing(true)
            try {
              await refreshSiteContent(project, site.slug)
              toast.success('Site republished with current content')
            } catch (err) {
              toast.error(contentErr(err, 'Could not republish the site'))
            } finally { setRefreshing(false) }
          }}
        >
          {refreshing ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />} Republish content now
        </button>
      </div>
    </div>
  )
}

// ── New collection from a kind ──────────────────────────────────────────

function NewCollection({ project, presets, sites, onClose, onCreated }: {
  project: string; presets: ContentPreset[]; sites: ContentSite[]
  onClose: () => void; onCreated: () => void
}) {
  const [kind, setKind] = useState<string>('article')
  const preset = presets.find(p => p.id === kind)
  const [singular, setSingular] = useState('')
  const [plural, setPlural] = useState('')
  const [objName, setObjName] = useState('')
  const [objTouched, setObjTouched] = useState(false)
  const [route, setRoute] = useState('')
  const [routeTouched, setRouteTouched] = useState(false)
  const [site, setSite] = useState(sites[0]?.slug || '')
  const [org, setOrg] = useState('')
  const [intro, setIntro] = useState('')
  const [role, setRole] = useState('content-editor')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!preset) return
    setSingular(preset.singular)
    setPlural(preset.plural)
    if (!routeTouched) setRoute(preset.route)
    if (!objTouched) setObjName(slugify(preset.plural).replace(/-/g, '_'))
  }, [preset, routeTouched, objTouched])

  const create = async () => {
    setBusy(true)
    try {
      const res = await createCollection(project, {
        kind, obj_name: objName.trim(), singular: singular.trim(), plural: plural.trim(),
        route: route.trim(), site_slug: site || null, grant_role: role.trim() || null,
        organization: org.trim(), intro: intro.trim(),
      })
      toast.success(res.pages.length
        ? `${plural} created — ${res.pages.length} page template(s) added to the site. Publish the site once to put them live.`
        : `${plural} created`)
      onCreated()
    } catch (err) {
      toast.error(contentErr(err, 'Could not create the collection'))
    } finally { setBusy(false) }
  }

  return (
    <div className="card" style={{ padding: 18, marginBottom: 16, borderLeft: '3px solid var(--color-accent)' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <strong style={{ flex: 1 }}>New collection</strong>
        <button className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close"><X size={14} /></button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10, marginBottom: 16 }}>
        {presets.map(p => (
          <button
            key={p.id} type="button" onClick={() => setKind(p.id)}
            className="card"
            style={{
              textAlign: 'left', padding: 12, cursor: 'pointer',
              border: `1px solid ${kind === p.id ? 'var(--color-accent)' : 'var(--color-border)'}`,
              background: kind === p.id ? 'var(--color-accent-weak, rgba(76,141,255,0.08))' : undefined,
            }}
          >
            <div style={{ fontWeight: 600, fontSize: 14, display: 'flex', gap: 6, alignItems: 'center' }}>
              {kind === p.id && <Check size={13} />} {p.label}
            </div>
            <div style={{ ...muted, fontSize: 13, marginTop: 4 }}>{p.description}</div>
          </button>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
        <div>
          <label className="form-label">One record is called</label>
          <input className="form-input" value={singular} onChange={e => setSingular(e.target.value)} />
        </div>
        <div>
          <label className="form-label">Many are called</label>
          <input className="form-input" value={plural} onChange={e => setPlural(e.target.value)} />
        </div>
        <div>
          <label className="form-label">Transaction name</label>
          <input className="form-input" value={objName}
            onChange={e => { setObjTouched(true); setObjName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_')) }} />
          <small style={{ ...muted, fontSize: 13 }}>Also the table name. Lower case, letters, digits and _.</small>
        </div>
        <div>
          <label className="form-label">Web address</label>
          <input className="form-input" value={route}
            onChange={e => { setRouteTouched(true); setRoute(slugify(e.target.value)) }} />
          <small style={{ ...muted, fontSize: 13 }}>/{route || '…'}/ lists them, /{route || '…'}/&lt;slug&gt;/ shows one.</small>
        </div>
        <div>
          <label className="form-label">Add pages to site</label>
          <select className="form-input" value={site} onChange={e => setSite(e.target.value)}>
            <option value="">— Don’t create pages yet —</option>
            {sites.map(s => <option key={s.slug} value={s.slug}>{s.title || s.slug}</option>)}
          </select>
        </div>
        <div>
          <label className="form-label">Organisation name</label>
          <input className="form-input" value={org} onChange={e => setOrg(e.target.value)} placeholder="Acme Ltd" />
          <small style={{ ...muted, fontSize: 13 }}>Publisher / employer in structured data.</small>
        </div>
        <div>
          <label className="form-label">Editors’ role</label>
          <input className="form-input" value={role} onChange={e => setRole(e.target.value)} />
          <small style={{ ...muted, fontSize: 13 }}>Granted full access to these records. Invite people with this role from the running application’s Users screen.</small>
        </div>
      </div>
      <div style={{ marginTop: 14 }}>
        <label className="form-label">Introduction on the list page</label>
        <input className="form-input" value={intro} onChange={e => setIntro(e.target.value)}
          placeholder={`All our ${plural.toLowerCase() || 'items'}.`} />
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
        <button className="btn btn-primary btn-sm" disabled={busy || !objName || !plural}
          onClick={() => void create()}>
          {busy ? 'Creating…' : `Create ${plural || 'collection'}`}
        </button>
        <span style={{ ...muted, alignSelf: 'center' }}>
          Creates the transaction, its menu entry and grant{site ? ', and the list / record / category pages' : ''}.
        </span>
      </div>
    </div>
  )
}

// ── Profile editor (existing transaction, or an enabled collection) ─────

const ROLE_ORDER = ['slug', 'title', 'summary', 'body', 'image', 'image_alt', 'published_at',
  'updated_at', 'author', 'sort', 'old_slugs', 'price', 'currency', 'sku', 'availability',
  'brand', 'start_date', 'end_date', 'location', 'employment_type', 'valid_through',
  'job_title', 'email', 'phone']

function ProfileEditor({ project, objects, sites, initialObj, roleHelp, sdTypes, presets, onClose, onSaved }: {
  project: string; objects: ContentObject[]; sites: ContentSite[]; initialObj: string
  roleHelp: Record<string, string>; sdTypes: string[]; presets: ContentPreset[]
  onClose: () => void; onSaved: () => void
}) {
  const [obj, setObj] = useState(initialObj)
  const [columns, setColumns] = useState<ColumnInfo[]>([])
  const [prof, setProf] = useState<ContentProfile>({})
  const [errors, setErrors] = useState<string[]>([])
  const [warnings, setWarnings] = useState<string[]>([])
  const [site, setSite] = useState('')
  const [createPages, setCreatePages] = useState(false)
  const [busy, setBusy] = useState(false)
  const isExisting = !!initialObj

  useEffect(() => {
    if (!obj) return
    let cancelled = false
    getContentObject(project, obj).then(r => {
      if (cancelled) return
      setColumns(r.columns)
      const base: ContentProfile = r.profile || guessProfile(r.columns, obj)
      setProf({ ...base, enabled: true })
      setErrors(r.errors)
      setWarnings(r.warnings)
    }).catch(err => toast.error(contentErr(err, 'Could not load the transaction')))
    return () => { cancelled = true }
  }, [project, obj])

  // Re-validate as the mapping changes.
  useEffect(() => {
    if (!obj || !prof.roles) return
    const t = setTimeout(() => {
      validateContentProfile(project, obj, prof)
        .then(r => { setErrors(r.errors); setWarnings(r.warnings) })
        .catch(() => undefined)
    }, 300)
    return () => clearTimeout(t)
  }, [project, obj, prof])

  const colOptions = useMemo(() => [{ name: '', heading: '— none —' }, ...columns], [columns])
  const setRole = (role: string, col: string) => setProf(p => {
    const roles = { ...(p.roles || {}) }
    if (col) roles[role] = col; else delete roles[role]
    return { ...p, roles }
  })
  const vis = prof.visibility || {}
  const setVis = (patch: Partial<NonNullable<ContentProfile['visibility']>>) =>
    setProf(p => ({ ...p, visibility: { ...(p.visibility || {}), ...patch } }))
  const tax = prof.taxonomies || []
  const setTax = (next: ContentTaxonomy[]) => setProf(p => ({ ...p, taxonomies: next }))

  const colSelect = (value: string | undefined, onChange: (v: string) => void) => (
    <select className="form-input" value={value || ''} onChange={e => onChange(e.target.value)}>
      {colOptions.map(c => (
        <option key={c.name || '_none'} value={c.name || ''}>
          {c.name ? `${c.heading || c.name} (${c.name}${'col_type' in c && c.col_type ? `, ${c.col_type}` : ''})` : c.heading}
        </option>
      ))}
    </select>
  )

  const save = async () => {
    setBusy(true)
    try {
      const res = await enableCollection(project, {
        obj_name: obj, profile: prof, site_slug: site || null, create_pages: createPages && !!site,
      })
      toast.success(res.pages.length ? `Publishing saved — ${res.pages.length} page(s) added` : 'Publishing saved')
      onSaved()
    } catch (err) {
      toast.error(contentErr(err, 'Could not save the publishing settings'))
    } finally { setBusy(false) }
  }

  return (
    <div className="card" style={{ padding: 18, marginBottom: 16, borderLeft: '3px solid var(--color-accent)' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <strong style={{ flex: 1 }}>{isExisting ? `Publishing: ${obj}` : 'Publish an existing transaction'}</strong>
        <button className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close"><X size={14} /></button>
      </div>
      {!isExisting && (
        <div style={{ marginBottom: 14, maxWidth: 360 }}>
          <label className="form-label">Transaction</label>
          <select className="form-input" value={obj} onChange={e => setObj(e.target.value)}>
            <option value="">— Choose —</option>
            {objects.map(o => <option key={o.obj_name} value={o.obj_name}>{o.title} ({o.obj_name})</option>)}
          </select>
        </div>
      )}
      {obj && prof.roles && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            <div>
              <label className="form-label">Kind</label>
              <select className="form-input" value={prof.kind || 'custom'}
                onChange={e => setProf(p => ({ ...p, kind: e.target.value }))}>
                {presets.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">One record is called</label>
              <input className="form-input" value={prof.label?.singular || ''}
                onChange={e => setProf(p => ({ ...p, label: { ...(p.label || {}), singular: e.target.value } }))} />
            </div>
            <div>
              <label className="form-label">Many are called</label>
              <input className="form-input" value={prof.label?.plural || ''}
                onChange={e => setProf(p => ({ ...p, label: { ...(p.label || {}), plural: e.target.value } }))} />
            </div>
            <div>
              <label className="form-label">Web address</label>
              <input className="form-input" value={prof.routes?.index || ''}
                onChange={e => setProf(p => ({ ...p, routes: { index: slugify(e.target.value), item: `${slugify(e.target.value)}/:slug` } }))} />
            </div>
          </div>

          <h3 style={{ fontSize: 14, fontWeight: 600, margin: '18px 0 8px' }}>Which column is which</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
            {ROLE_ORDER.map(role => (
              <div key={role}>
                <label className="form-label" title={roleHelp[role]}>
                  {role.replace(/_/g, ' ')}{(role === 'slug' || role === 'title') && <span style={{ color: '#ef4444' }}> *</span>}
                </label>
                {colSelect(prof.roles?.[role], v => setRole(role, v))}
                <small style={{ ...muted, fontSize: 13 }}>{roleHelp[role]}</small>
              </div>
            ))}
          </div>

          <h3 style={{ fontSize: 14, fontWeight: 600, margin: '18px 0 8px' }}>When is a record public?</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            <div>
              <label className="form-label">Status column</label>
              {colSelect(vis.status_column, v => setVis({ status_column: v || undefined }))}
            </div>
            <div>
              <label className="form-label">Public when status is</label>
              <input className="form-input" value={(vis.live_values || []).join(', ')}
                placeholder="published"
                onChange={e => setVis({ live_values: e.target.value.split(',').map(x => x.trim()).filter(Boolean) })} />
            </div>
            <div>
              <label className="form-label">…and this date has passed</label>
              {colSelect(vis.schedule_column, v => setVis({ schedule_column: v || undefined }))}
            </div>
          </div>

          <h3 style={{ fontSize: 14, fontWeight: 600, margin: '18px 0 8px' }}>Search engines</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            <div>
              <label className="form-label">Structured data</label>
              <select className="form-input" value={prof.structured_data?.type || 'none'}
                onChange={e => setProf(p => ({ ...p, structured_data: { ...(p.structured_data || {}), type: e.target.value } }))}>
                {sdTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            {(['title', 'description', 'canonical', 'noindex', 'og_image'] as const).map(k => (
              <div key={k}>
                <label className="form-label">SEO {k.replace('_', ' ')} column</label>
                {colSelect(prof.seo?.[k], v => setProf(p => {
                  const seo = { ...(p.seo || {}) }
                  if (v) seo[k] = v; else delete seo[k]
                  return { ...p, seo }
                }))}
              </div>
            ))}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, alignSelf: 'end' }}>
              <input type="checkbox" checked={!!prof.feed} onChange={e => setProf(p => ({ ...p, feed: e.target.checked }))} />
              RSS feed
            </label>
          </div>

          <h3 style={{ fontSize: 14, fontWeight: 600, margin: '18px 0 8px' }}>Categories and tags</h3>
          {tax.map((t, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
              <input className="form-input" style={{ width: 160 }} value={t.label} placeholder="Label"
                onChange={e => setTax(tax.map((x, j) => j === i ? { ...x, label: e.target.value, id: slugify(e.target.value) || x.id } : x))} />
              <div style={{ width: 260 }}>{colSelect(t.column, v => setTax(tax.map((x, j) => j === i ? { ...x, column: v } : x)))}</div>
              <label style={{ fontSize: 13, display: 'flex', gap: 4, alignItems: 'center' }}>
                <input type="checkbox" checked={!!t.multi}
                  onChange={e => setTax(tax.map((x, j) => j === i ? { ...x, multi: e.target.checked } : x))} />
                Several per record (comma-separated)
              </label>
              <button className="btn btn-ghost btn-sm" onClick={() => setTax(tax.filter((_, j) => j !== i))}><X size={13} /></button>
            </div>
          ))}
          <button className="btn btn-ghost btn-sm" onClick={() => setTax([...tax, { id: `group-${tax.length + 1}`, column: '', label: 'Category' }])}>
            <Plus size={13} /> Add a grouping
          </button>

          {!isExisting && sites.length > 0 && (
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 16, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 14 }}>
                <input type="checkbox" checked={createPages} onChange={e => setCreatePages(e.target.checked)} />
                Also create list / record / category pages on
              </label>
              <select className="form-input" style={{ width: 220 }} value={site} onChange={e => setSite(e.target.value)}>
                <option value="">— Choose a site —</option>
                {sites.map(s => <option key={s.slug} value={s.slug}>{s.title || s.slug}</option>)}
              </select>
            </div>
          )}

          {(errors.length > 0 || warnings.length > 0) && (
            <div style={{ marginTop: 14 }}>
              {errors.map(e => <div key={e} style={{ color: 'var(--color-error, #dc2626)', fontSize: 13 }}>✕ {e}</div>)}
              {warnings.map(w => <div key={w} style={{ color: 'var(--color-warning, #d97706)', fontSize: 13 }}>⚠ {w}</div>)}
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button className="btn btn-primary btn-sm" disabled={busy || errors.length > 0} onClick={() => void save()}>
              {busy ? 'Saving…' : 'Save publishing'}
            </button>
          </div>
        </>
      )}
    </div>
  )
}

/** A first guess at the mapping for a transaction that has none, from its
 *  column names — the author confirms or changes every one of them. */
function guessProfile(columns: ColumnInfo[], obj: string): ContentProfile {
  const names = columns.map(c => c.name)
  const find = (...cands: string[]) => names.find(n => cands.includes(n.toLowerCase())) || ''
  const pick = (re: RegExp) => names.find(n => re.test(n.toLowerCase())) || ''
  const rich = columns.find(c => String(c.col_type || '').toUpperCase() === 'RICHTEXT')?.name || ''
  const roles: Record<string, string> = {}
  const set = (role: string, v: string) => { if (v) roles[role] = v }
  set('title', find('title', 'name', 'headline') || pick(/(^|_)(title|name)$/))
  set('slug', find('slug', 'url_slug') || pick(/slug/))
  set('summary', find('summary', 'excerpt', 'descr', 'description', 'subtitle'))
  set('body', rich || find('body', 'content', 'details'))
  set('image', find('image', 'image_url', 'cover_image', 'photo') || pick(/image|photo/))
  set('published_at', find('published_at', 'publish_date'))
  set('updated_at', find('updated_at', 'chg_date'))
  set('price', find('price', 'unit_price'))
  set('sku', find('sku', 'code', 'product_code'))
  const status = find('status', 'state')
  return {
    enabled: true, kind: 'custom',
    label: { singular: obj.replace(/_/g, ' '), plural: obj.replace(/_/g, ' ') },
    roles, visibility: status ? { status_column: status, live_values: ['published'] } : {},
    seo: {}, structured_data: { type: 'none' }, taxonomies: [], feed: false,
    routes: { index: slugify(obj), item: `${slugify(obj)}/:slug` },
  }
}
